// Cliente HTTP e constantes compartilhadas da plataforma Flow

export const flowFetch = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    (headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  const response = await fetch(url, { ...options, headers });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || `Erro ${response.status}`);
  return data;
};

export interface FlowUser { id: string; nome: string; email: string; }

export interface FlowArea {
  id: string; name: string; description?: string | null;
  color: string; icon: string; archived: boolean;
  members?: { userId: string; role: string; user?: FlowUser | null }[];
  openTasks?: number;
}

export interface FlowBoard {
  id: string; areaId: string; name: string; position: number; archived: boolean;
  openTasks?: number;
}

export interface FlowColumn {
  id: string; boardId: string; name: string; kind: string; position: number; archived: boolean;
}

export interface FlowChecklistItem { id?: string; title: string; done: boolean; position?: number; createdById?: string | null; }

export interface FlowAttachment {
  id: string; fileName: string; url: string; mimeType?: string | null; size?: number | null;
  uploadedById: string; uploadedBy?: FlowUser | null; createdAt: string;
}

export interface FlowTask {
  id: string; title: string; description?: string | null;
  areaId?: string | null; boardId?: string | null; columnId?: string | null; position?: number;
  assigneeId?: string | null; createdById: string;
  dueDate?: string | null; status: string; priority: string; visibility: string;
  completedAt?: string | null; createdAt: string;
  timeSpentSeconds?: number; timerStartedAt?: string | null;
  isDaily?: boolean; resetTime?: string | null;
  checklist?: FlowChecklistItem[];
  participants?: { userId: string; user?: FlowUser | null }[];
  assignee?: FlowUser | null; createdBy?: FlowUser | null;
  comments?: FlowComment[];
  activities?: FlowActivity[];
  // agregados para exibição
  area?: { id: string; name: string; color?: string } | null;
  board?: { id: string; name: string } | null;
  boardColumns?: FlowColumn[];
  attachments?: FlowAttachment[];
  checklistTotal?: number; checklistDone?: number; commentCount?: number;
}

export interface FlowDocument {
  id: string; title: string; category: string; areaId?: string | null;
  content?: string; preview?: string; visibility: string;
  createdById: string; createdBy?: FlowUser | null; updatedAt: string; createdAt: string;
  shares?: { userId: string; user?: FlowUser | null }[];
  comments?: FlowComment[];
}

export interface FlowComment {
  id: string; taskId?: string | null; documentId?: string | null;
  authorId: string; author?: FlowUser | null; content: string;
  mentions?: string[] | null; createdAt: string; contextTitle?: string | null; mentioned?: boolean;
}

export interface FlowActivity {
  id: string; type: string; message: string; userId: string;
  user?: FlowUser | null; createdAt: string;
  taskId?: string | null; documentId?: string | null; areaId?: string | null;
}

export const STATUS_LABELS: Record<string, string> = {
  TODO: 'A fazer', DOING: 'Fazendo', WAITING: 'Aguardando', DONE: 'Concluído'
};
export const STATUS_COLORS: Record<string, string> = {
  TODO: 'bg-slate-100 text-slate-700',
  DOING: 'bg-blue-100 text-blue-700',
  WAITING: 'bg-amber-100 text-amber-700',
  DONE: 'bg-emerald-100 text-emerald-700'
};
export const PRIORITY_LABELS: Record<string, string> = {
  LOW: 'Baixa', NORMAL: 'Normal', HIGH: 'Alta', URGENT: 'Urgente'
};
export const PRIORITY_COLORS: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-500',
  NORMAL: 'bg-sky-100 text-sky-700',
  HIGH: 'bg-orange-100 text-orange-700',
  URGENT: 'bg-red-100 text-red-700'
};
export const CATEGORY_LABELS: Record<string, string> = {
  REUNIAO: 'Reunião', PROCESSO: 'Processo', ATENDIMENTO: 'Atendimento',
  PRODUCAO: 'Produção', EXPEDICAO: 'Expedição', FINANCEIRO: 'Financeiro',
  CAMPANHA: 'Campanha', TREINAMENTO: 'Treinamento', ACESSO: 'Acesso', GERAL: 'Geral'
};

export const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
    (d.getHours() || d.getMinutes() ? ` ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : '');
};

export const formatDuration = (secs?: number) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
};

// Tempo total incluindo o cronômetro em andamento (se estiver rodando)
export const taskElapsed = (t: { timeSpentSeconds?: number; timerStartedAt?: string | null }) => {
  let s = t.timeSpentSeconds || 0;
  if (t.timerStartedAt) s += Math.max(0, Math.floor((Date.now() - new Date(t.timerStartedAt).getTime()) / 1000));
  return s;
};

export const isOverdue = (task: Pick<FlowTask, 'dueDate' | 'status'>) =>
  !!task.dueDate && task.status !== 'DONE' && new Date(task.dueDate).getTime() < Date.now();
